import React, { useEffect, useRef, useState } from "react";
import {
    Alert,
    Badge,
    Card,
    Col,
    Container,
    Row,
    Spinner,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import DynamicBreadcrumb from "../header/breadcrumb";
import customerApi from "../../services/customerapi";
import { loadGoogleMaps } from "../../utils/googleMapsLoader";

const normalizeGeojson = (geojson) => {
    if (!geojson) {
        return null;
    }

    if (typeof geojson === "string") {
        try {
            return JSON.parse(geojson);
        } catch (error) {
            console.error("Invalid zone GeoJSON:", error);
            return null;
        }
    }

    return geojson;
};

const geojsonToPath = (geojson) => {
    const geo = normalizeGeojson(geojson);

    const geometry =
        geo?.type === "Feature"
            ? geo.geometry
            : geo;

    if (!geometry) {
        return [];
    }

    if (geometry.type !== "Polygon") {
        return [];
    }

    const ring = geometry.coordinates?.[0] || [];

    return ring
        .map(([lng, lat]) => ({
            lat: Number(lat),
            lng: Number(lng),
        }))
        .filter(
            (point) =>
                Number.isFinite(point.lat)
                && Number.isFinite(point.lng)
        );
};

const hasValidCoordinate = (value) => {
    if (
        value === null
        || value === undefined
        || String(value).trim() === ""
    ) {
        return false;
    }

    return Number.isFinite(Number(value));
};

export default function CustomerAddressLocationPage() {
    const { addressId } = useParams();

    const mapDivRef = useRef(null);
    const mapRef = useRef(null);
    const polygonsRef = useRef([]);
    const markersRef = useRef([]);
    const infoWindowRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);

    const clearMapObjects = () => {
        polygonsRef.current.forEach((polygon) => {
            polygon.setMap(null);
        });

        markersRef.current.forEach((marker) => {
            marker.setMap(null);
        });

        polygonsRef.current = [];
        markersRef.current = [];

        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }
    };

    const drawMap = async (payload) => {
        try {
            const google = await loadGoogleMaps([]);

            if (!mapDivRef.current) {
                return;
            }

            if (!mapRef.current) {
                mapRef.current = new google.maps.Map(
                    mapDivRef.current,
                    {
                        center: {
                            lat: 13.0827,
                            lng: 80.2707,
                        },
                        zoom: 11,
                        mapTypeControl: true,
                        streetViewControl: false,
                        fullscreenControl: true,
                        zoomControl: true,
                    }
                );

                infoWindowRef.current =
                    new google.maps.InfoWindow();
            }

            clearMapObjects();

            const bounds = new google.maps.LatLngBounds();
            let hasBounds = false;

            /*
             * Zone colours:
             *
             * Green  = API confirmed customer is inside the zone.
             * Orange = Zone belongs to nearest branch only.
             * Blue   = Other delivery zones.
             */
            (payload?.allZones || []).forEach((zone) => {
                const path = geojsonToPath(zone.geojson);

                if (!path.length) {
                    return;
                }

                const isMatched =
                    Number(zone.id)
                    === Number(payload?.matchedZone?.id);

                const isNearestBranch =
                    !payload?.matchedZone
                    && Number(zone.branchId)
                        === Number(payload?.branchId);

                const polygon = new google.maps.Polygon({
                    paths: path,
                    map: mapRef.current,
                    clickable: true,
                    editable: false,
                    draggable: false,

                    strokeColor: isMatched
                        ? "#15803d"
                        : isNearestBranch
                            ? "#f59e0b"
                            : "#2563eb",

                    fillColor: isMatched
                        ? "#22c55e"
                        : isNearestBranch
                            ? "#fbbf24"
                            : "#60a5fa",

                    strokeOpacity: 0.95,

                    fillOpacity: isMatched
                        ? 0.32
                        : isNearestBranch
                            ? 0.18
                            : 0.10,

                    strokeWeight: isMatched
                        ? 4
                        : isNearestBranch
                            ? 3
                            : 2,

                    zIndex: isMatched
                        ? 99
                        : isNearestBranch
                            ? 50
                            : 1,
                });

                polygon.addListener("click", (event) => {
                    const zoneRelationText = isMatched
                        ? "Matched customer zone"
                        : isNearestBranch
                            ? "Nearest branch zone — customer is not confirmed inside"
                            : "Delivery zone";

                    infoWindowRef.current.setContent(`
                        <div style="min-width:220px;line-height:1.6;">
                            <div style="font-weight:700;">
                                ${zone.branchName || "-"}
                            </div>

                            <div>
                                Branch ID: ${zone.branchId || "-"}
                            </div>

                            <div>
                                Zone: ${
                                    zone.zoneName
                                    || zone.zoneType
                                    || "-"
                                }
                            </div>

                            <div>
                                Zone Type: ${zone.zoneType || "-"}
                            </div>

                            <div>
                                Priority: ${zone.priority ?? "-"}
                            </div>

                            <div style="margin-top:6px;font-weight:600;">
                                ${zoneRelationText}
                            </div>
                        </div>
                    `);

                    infoWindowRef.current.setPosition(
                        event.latLng
                    );

                    infoWindowRef.current.open({
                        map: mapRef.current,
                    });
                });

                polygonsRef.current.push(polygon);

                path.forEach((point) => {
                    bounds.extend(point);
                    hasBounds = true;
                });
            });

            const hasCustomerCoordinates =
                hasValidCoordinate(payload?.customerLat)
                && hasValidCoordinate(payload?.customerLng);

            if (hasCustomerCoordinates) {
                const customerPosition = {
                    lat: Number(payload.customerLat),
                    lng: Number(payload.customerLng),
                };

                const customerMarker =
                    new google.maps.Marker({
                        position: customerPosition,
                        map: mapRef.current,
                        title:
                            payload.customerName
                            || "Customer Location",
                        label: "C",
                    });

                customerMarker.addListener("click", () => {
                    infoWindowRef.current.setContent(`
                        <div style="min-width:240px;line-height:1.6;">
                            <div style="font-weight:700;">
                                ${
                                    payload.customerName
                                    || "Customer"
                                }
                            </div>

                            <div>
                                ${
                                    payload.shippingAddress
                                    || "-"
                                }
                            </div>

                            <div>
                                Lat: ${payload.customerLat}
                            </div>

                            <div>
                                Lng: ${payload.customerLng}
                            </div>
                        </div>
                    `);

                    infoWindowRef.current.open({
                        map: mapRef.current,
                        anchor: customerMarker,
                    });
                });

                markersRef.current.push(customerMarker);
                bounds.extend(customerPosition);
                hasBounds = true;
            }

            const hasBranchCoordinates =
                hasValidCoordinate(payload?.branchLat)
                && hasValidCoordinate(payload?.branchLng);

            if (hasBranchCoordinates) {
                const branchPosition = {
                    lat: Number(payload.branchLat),
                    lng: Number(payload.branchLng),
                };

                const branchMarker =
                    new google.maps.Marker({
                        position: branchPosition,
                        map: mapRef.current,
                        title:
                            payload.branchName
                            || "Branch",
                        label: "B",
                    });

                branchMarker.addListener("click", () => {
                    let branchRelationText =
                        "Nearest branch";

                    if (
                        payload.branchRelation
                        === "MATCHED_ZONE_BRANCH"
                    ) {
                        branchRelationText =
                            "Matched zone branch";
                    } else if (
                        payload.branchRelation
                        === "LOCATION_MISSING"
                    ) {
                        branchRelationText =
                            "Location unavailable";
                    }

                    infoWindowRef.current.setContent(`
                        <div style="min-width:220px;line-height:1.6;">
                            <div style="font-weight:700;">
                                ${payload.branchName || "-"}
                            </div>

                            <div>
                                Branch ID: ${payload.branchId || "-"}
                            </div>

                            <div>
                                Lat: ${payload.branchLat}
                            </div>

                            <div>
                                Lng: ${payload.branchLng}
                            </div>

                            <div style="margin-top:6px;font-weight:600;">
                                ${branchRelationText}
                            </div>
                        </div>
                    `);

                    infoWindowRef.current.open({
                        map: mapRef.current,
                        anchor: branchMarker,
                    });
                });

                markersRef.current.push(branchMarker);
                bounds.extend(branchPosition);
                hasBounds = true;
            }

            if (hasBounds) {
                mapRef.current.fitBounds(bounds);
            }
        } catch (mapError) {
            console.error(
                "Failed to draw customer address map:",
                mapError
            );

            setError(
                mapError?.message
                || "Failed to load customer address map"
            );
        }
    };

    useEffect(() => {
        let mounted = true;

        setLoading(true);
        setError("");

        customerApi
            .getCustomerAddressLocation(addressId)
            .then((response) => {
                if (!mounted) {
                    return;
                }

                if (response?.status !== "SUCCESS") {
                    throw new Error(
                        response?.message
                        || "Failed to load customer address location"
                    );
                }

                setData(response?.data || null);
            })
            .catch((err) => {
                if (!mounted) {
                    return;
                }

                setError(
                    err?.response?.data?.message
                    || err?.message
                    || "Failed to load customer address location"
                );
            })
            .finally(() => {
                if (mounted) {
                    setLoading(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, [addressId]);

    useEffect(() => {
        if (!loading && data) {
            const timer = setTimeout(() => {
                drawMap(data);
            }, 100);

            return () => {
                clearTimeout(timer);
            };
        }

        return undefined;
    }, [loading, data]);

    useEffect(() => {
        return () => {
            clearMapObjects();
            mapRef.current = null;
        };
    }, []);

    return (
        <Container fluid className="py-3">
            <DynamicBreadcrumb
                pathArray={[
                    {
                        name: "Customers",
                        url: "/admin/customers/all",
                    },
                    {
                        name: "Customer Addresses",
                        url: data?.customerId
                            ? `/admin/customers/${data.customerId}/addresses`
                            : null,
                    },
                    {
                        name: "Address Location",
                        url: null,
                    },
                ]}
            />

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                </div>
            ) : error ? (
                <Alert variant="danger">
                    {error}
                </Alert>
            ) : !data ? (
                <Alert variant="warning">
                    No location data found.
                </Alert>
            ) : (
                <>
                    {data.zoneStatus === "LOCATION_MISSING" && (
                        <Alert variant="warning">
                            This address does not have valid
                            latitude and longitude.
                        </Alert>
                    )}

                    <Row className="g-3">
                        <Col md={8}>
                            <div
                                ref={mapDivRef}
                                style={{
                                    width: "100%",
                                    height: 700,
                                    borderRadius: 12,
                                    overflow: "hidden",
                                }}
                            />
                        </Col>

                        <Col md={4}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <h5>Customer Address</h5>

                                    <div>
                                        <strong>
                                            Customer:
                                        </strong>{" "}
                                        {data.customerName || "-"}
                                    </div>

                                    <div>
                                        <strong>
                                            Mobile:
                                        </strong>{" "}
                                        {data.customerMobile || "-"}
                                    </div>

                                    <div>
                                        <strong>
                                            Address Name:
                                        </strong>{" "}
                                        {data.addressName || "-"}
                                    </div>

                                    <div className="mt-2">
                                        <strong>
                                            Address:
                                        </strong>
                                    </div>

                                    <div className="small text-muted">
                                        {data.shippingAddress || "-"}
                                    </div>
                                </Card.Body>
                            </Card>

                            <Card className="mb-3">
                                <Card.Body>
                                    <h5>Coordinates</h5>

                                    <div>
                                        <strong>
                                            Customer Lat:
                                        </strong>{" "}
                                        {data.customerLat ?? "-"}
                                    </div>

                                    <div>
                                        <strong>
                                            Customer Lng:
                                        </strong>{" "}
                                        {data.customerLng ?? "-"}
                                    </div>

                                    <div className="mt-2">
                                        <strong>
                                            Branch Lat:
                                        </strong>{" "}
                                        {data.branchLat ?? "-"}
                                    </div>

                                    <div>
                                        <strong>
                                            Branch Lng:
                                        </strong>{" "}
                                        {data.branchLng ?? "-"}
                                    </div>
                                </Card.Body>
                            </Card>

                            <Card>
                                <Card.Body>
                                    <h5>Zone Result</h5>

                                    {data.zoneStatus
                                    === "LOCATION_MISSING" ? (
                                        <>
                                            <Badge
                                                bg="warning"
                                                text="dark"
                                                className="mb-2"
                                            >
                                                Location Missing
                                            </Badge>

                                            <div className="text-muted">
                                                Valid latitude and
                                                longitude are not
                                                available for this
                                                address.
                                            </div>
                                        </>
                                    ) : data.matchedZone ? (
                                        <>
                                            <Badge
                                                bg="success"
                                                className="mb-2"
                                            >
                                                Inside Zone
                                            </Badge>

                                            <div>
                                                <strong>
                                                    Matched Branch:
                                                </strong>{" "}
                                                {
                                                    data.matchedZone
                                                        .branchName
                                                    || "-"
                                                }
                                            </div>

                                            <div>
                                                <strong>
                                                    Branch ID:
                                                </strong>{" "}
                                                {
                                                    data.matchedZone
                                                        .branchId
                                                    || "-"
                                                }
                                            </div>

                                            <div>
                                                <strong>
                                                    Zone:
                                                </strong>{" "}
                                                {
                                                    data.matchedZone
                                                        .zoneName
                                                    || data.matchedZone
                                                        .zoneType
                                                    || "-"
                                                }
                                            </div>

                                            <div>
                                                <strong>
                                                    Zone Type:
                                                </strong>{" "}
                                                {
                                                    data.matchedZone
                                                        .zoneType
                                                    || "-"
                                                }
                                            </div>

                                            <div>
                                                <strong>
                                                    Priority:
                                                </strong>{" "}
                                                {
                                                    data.matchedZone
                                                        .priority
                                                    ?? "-"
                                                }
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <Badge
                                                bg="danger"
                                                className="mb-2"
                                            >
                                                Outside All Zones
                                            </Badge>

                                            <div>
                                                <strong>
                                                    Nearest Branch:
                                                </strong>{" "}
                                                {data.branchName || "-"}
                                            </div>

                                            <div>
                                                <strong>
                                                    Branch ID:
                                                </strong>{" "}
                                                {data.branchId || "-"}
                                            </div>

                                            <div>
                                                <strong>
                                                    Approx. Distance:
                                                </strong>{" "}
                                                {
                                                    data.branchDistanceKm
                                                        !== null
                                                    && data.branchDistanceKm
                                                        !== undefined
                                                        ? `${data.branchDistanceKm} km`
                                                        : "-"
                                                }
                                            </div>

                                            <div className="small text-muted mt-2">
                                                The highlighted orange
                                                zone belongs to the
                                                nearest branch. It does
                                                not confirm that the
                                                customer is inside the
                                                zone.
                                            </div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </Container>
    );
}